import { MainLayout } from 'components/Layout';
import { useEffect } from 'react';

export const TermsAndConditions = () => {
  useEffect(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth', // change to 'auto' if you want instant
      });
    }, []);
  return (
    <MainLayout
      title="Terms & Conditions | Fundo"
      description="Terms governing your use of the Fundo platform."
    >
      {/* HEADER */}
      <section className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.35em] text-[#8b5e3c]">Terms & Conditions</p>

        <h1 className="mt-4 font-serif text-5xl leading-[0.95] tracking-[-0.04em] text-[#1b140f] sm:text-6xl">
          Terms that govern your use of Fundo.
        </h1>

        <p className="mt-6 text-lg leading-8 text-[#4f4034]">
          These Terms and Conditions outline the rules and responsibilities for using the Fundo
          platform.
        </p>

        <p className="mt-4 text-sm text-[#8b5e3c]">Last updated: April 2026</p>
      </section>

      {/* CONTENT */}
      <section className="mt-20 max-w-3xl space-y-14">
        {/* ACCEPTANCE */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">1. Acceptance of Terms</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            By accessing or using Fundo, you agree to be bound by these Terms. If you do not agree,
            you should not use the platform.
          </p>
        </div>

        {/* ELIGIBILITY */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">2. Eligibility</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            You must have the legal capacity to enter into a binding agreement to use Fundo. By
            using the platform, you confirm that the information you provide is accurate and
            complete.
          </p>
        </div>

        {/* PLATFORM USE */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">3. Use of the Platform</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            Fundo provides tools for farm management, virtual farming, marketplace transactions, and
            financial services. You agree to use the platform only for lawful purposes and in
            accordance with these Terms.
          </p>
        </div>

        {/* ACCOUNTS */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">4. User Accounts</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            You are responsible for maintaining the confidentiality of your account credentials and
            for all activities that occur under your account.
          </p>
        </div>

        {/* MARKETPLACE */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">5. Marketplace & Transactions</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            Fundo facilitates transactions between users but does not take ownership of goods. Users
            are responsible for the accuracy of listings, pricing, and fulfillment of transactions.
          </p>
        </div>

        {/* VIRTUAL FARMING */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">6. Virtual Farming Participation</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            Fundo may enable participation in agricultural activities without direct ownership of
            land. Outcomes may vary due to real-world factors such as weather, market conditions,
            and operational risks. Fundo does not guarantee returns or outcomes from such
            participation.
          </p>
        </div>

        {/* FINANCIAL SERVICES */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">7. Financial Services</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            Financial services offered through Fundo may be provided by third-party partners.
            Approval, terms, and conditions are determined by those providers.
          </p>
        </div>

        {/* USSD/SMS */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">8. SMS and USSD Usage</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            Accessing Fundo via SMS or USSD may involve network charges from your mobile operator.
            Fundo is not responsible for such charges or network limitations.
          </p>
        </div>

        {/* PROHIBITED USE */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">9. Prohibited Activities</h2>
          <ul className="mt-4 space-y-3 text-sm text-[#4f4034]">
            <li>• Misrepresentation of farm data or products</li>
            <li>• Fraudulent transactions or activity</li>
            <li>• Unauthorized access to the platform</li>
            <li>• Use of the platform for illegal purposes</li>
          </ul>
        </div>

        {/* LIABILITY */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">10. Limitation of Liability</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            Fundo is provided “as is” without warranties of any kind. We are not liable for losses
            resulting from platform use, including but not limited to financial loss, data loss, or
            agricultural outcomes.
          </p>
        </div>

        {/* TERMINATION */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">11. Termination</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            We reserve the right to suspend or terminate accounts that violate these Terms or misuse
            the platform.
          </p>
        </div>

        {/* CHANGES */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">12. Changes to Terms</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            These Terms may be updated periodically. Continued use of Fundo constitutes acceptance
            of the updated Terms.
          </p>
        </div>

        {/* GOVERNING LAW */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">13. Governing Law</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            These Terms are governed by applicable laws in the jurisdictions where Fundo operates.
          </p>
        </div>

        {/* CONTACT */}
        <div>
          <h2 className="font-serif text-2xl text-[#1b140f]">14. Contact</h2>
          <p className="mt-4 text-[#4f4034] leading-8">
            For questions regarding these Terms, contact:
          </p>

          <p className="mt-4 text-sm text-[#8b5e3c]">fundofarmer@gmail.com</p>
        </div>
      </section>
    </MainLayout>
  );
};
